import type { TInputType } from '@alfa-bank/site-pure-input/src/models';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import type { FC } from 'react';
import React, { memo } from 'react';
import cn from 'classnames';

import type { TPromoCode } from './models';

export const MAX_PROMOCODE_LENGTH = 10;

export const PromoCode: FC<TPromoCode> = memo(
    ({
        Caption,
        Field,
        Input,
        Link,
        formatPromoCode,
        getCaption,
        isError,
        isSuccess,
        name,
        settings,
        styles,
        validatePromoCode,
    }) => {
        const {
            caption: inputCaption,
            captionLink,
            customErrorMessage,
            dataTestId,
            listOfPromoValues,
            optional = true,
            placeholder,
        } = settings;

        return (
            <Field
                data={settings}
                name={name}
                render={({ input, meta }) => {
                    const caption = getCaption(meta, inputCaption);
                    const error = isError(meta);

                    return (
                        <div className={styles.promoCode}>
                            <Input
                                {...input}
                                dataTestId={dataTestId ?? name}
                                error={error}
                                forwardedRef={actions.getRef(meta)}
                                name={name}
                                onBlur={actions.onBlur(input)}
                                onCancel={() => {
                                    actions.onCancel({ input, meta });
                                    actions.onChange(input)('');
                                }}
                                onChange={({ value }) => {
                                    actions.onChange(input)(
                                        formatPromoCode(String(value), MAX_PROMOCODE_LENGTH)
                                    );
                                }}
                                onFocus={actions.onFocus(input)}
                                placeholder={placeholder}
                                success={!!input.value && isSuccess(meta)}
                                type={input.type as TInputType}
                                value={input.value}
                            />
                            {caption && (
                                <Caption
                                    className={cn(styles.external, styles.caption, {
                                        [styles.captionError]: error,
                                    })}
                                    size="s"
                                    theme="white"
                                >
                                    {caption}
                                    {captionLink && !error && (
                                        <Link
                                            href={captionLink?.src}
                                            kind="caption"
                                            target="_blank"
                                        >
                                            {captionLink?.name}
                                        </Link>
                                    )}
                                </Caption>
                            )}
                        </div>
                    );
                }}
                validate={validatePromoCode(optional, listOfPromoValues, customErrorMessage)}
            />
        );
    }
);
